/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnDef } from '@tanstack/react-table';
import { useMemo, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTable from 'components/base/AdvanceTable';
import orderOutService from 'service/orderOut';
import { OutgoingType } from 'data/outgoing';
import legalEntitySettingsService from 'service/legalEntitySettings';
import { pageToOffset } from 'utils/pageToOffset';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';
import ExcelIcon from 'pages/reports/purchase/filter/ExcelIcon';
import Button from 'components/base/Button';

interface OutgoingTableProps {
  dateFrom?: any;
  dateTo?: any;
  optionSenderId?: any;
  optionStatusId?: any;
  optionDelivererId?: any;
}

const OutgoingTable = ({
  dateFrom,
  dateTo,
  optionSenderId,
  optionStatusId,
  optionDelivererId
}: OutgoingTableProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const url = process.env.REACT_APP_CDN_URL;

  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const navigateTo = (row: any) => {
    navigate(
      `/applications/outgoing/applications-result/${row?.original?.gupid}`,
      {
        state: {
          orders: row?.original?.orders
        }
      }
    );
  };

  const { data: dataSettings } = useQuery(['GET_LEGAL_ENTITY_SETTINGS'], () => {
    const res = legalEntitySettingsService
      .getList({
        offset: 0,
        limit: 100
      })
      .then((res: any) => res?.legal_entity_settings);
    return res;
  });

  /* prettier-ignore */
  const adjustedDateTo =
    !dateFrom && !dateTo
      ? undefined
      : dateTo === dateFrom
        ? dateFrom + 86400000
        : dateTo
          ? dateTo + 86400000
          : undefined;

  //Get Order
  const { data, refetch, isLoading, isFetching } = useQuery(
    [
      'GET_ORDER_OUT_MAIN',
      optionSenderId,
      optionStatusId,
      dateFrom,
      adjustedDateTo,
      currentPage,
      optionDelivererId
    ],
    async () => {
      const res = await orderOutService
        .getList({
          offset: pageToOffset(currentPage, 10),
          limit: 10,
          state: optionStatusId ? optionStatusId : undefined,
          sender_pid: optionSenderId ? optionSenderId : undefined,
          date_from: dateFrom || undefined,
          date_to: adjustedDateTo || undefined,
          deliver_employee_pid: optionDelivererId
            ? optionDelivererId
            : undefined
        })
        .then((res: any) => res);
      return res;
    },
    {
      enabled:
        !!dateFrom || !!adjustedDateTo || !!currentPage || !!optionDelivererId
    }
  );

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.count / 10));
    }
  }, [data]);

  const loading = isLoading || isFetching;

  /* prettier-ignore */
  const formatDateToTimeZone = (date: number, timeZone: string) => {
    try {
      // Format the date according to the specified time zone
      return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: timeZone
      }).format(new Date(date));
    } catch (error) {
      console.error('Error formatting date with time zone:', timeZone, error);
      return ''; // Return empty string if the time zone is invalid
    }
  };

  const outgoingData: OutgoingType[] = useMemo(() => {
    let timeZone = dataSettings?.[0]?.timeZone?.name?.ru ?? 'UTC';

    timeZone = timeZone.replace(/\s*\(.*?\)/, '').trim();

    return (
      data?.orders?.map((items: any) => {
        return {
          name: items?.name,
          orders: items?.order_number,
          status: items?.state_info?.name?.[i18n?.language],
          status_pid: items?.state_info?.pid,
          // errorRow: items?.state_info?.pid === 10,
          sender: items?.legal_entity?.name,
          currency: items?.currency?.name?.[i18n?.language],
          /* prettier-ignore */
          create_date: formatDateToTimeZone(items?.order_date, timeZone),
          delivered_date: formatDateToTimeZone(items?.delivered_date, timeZone),
          recipient_approved_date: formatDateToTimeZone(
            items?.recipient_approved_date,
            timeZone
          ),
          can_update: items?.can_update ?? false,
          delivery:
            (items?.deliverer?.first_name || '') +
            ' ' +
            (items?.deliverer?.last_name || ''),
          gupid: items?.pid
        };
      }) ?? []
    );
  }, [data, dataSettings]);

  const handleExcelById = (pid: number) => {
    const customFilename: string = 'Закуп по накладной.xlsx';

    // Fetch report and trigger download
    orderOutService
      ?.getOrderOutListExcel({ order_pid: pid, order_type: 'OUT' })
      .then((res: any) => {
        if (res?.filename !== '') {
          const resUrl = `${url}${res?.filename}`;
          const link = document.createElement('a');
          link.href = resUrl;
          link.download = customFilename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      });
  };

  const outgoingTableColumns: ColumnDef<OutgoingType>[] = [
    {
      id: 'action1',
      cell: rowData => {
        const { can_update } = rowData.row.original;
        console.log('can_update', can_update);
        return (
          <div>
            {!can_update && (
              <Button
                variant="hover"
                onClick={(e: any) => {
                  handleExcelById(rowData?.row?.original?.gupid);
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <ExcelIcon />
              </Button>
            )}
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: '5%', textAlign: 'center' } },
        cellProps: { className: 'text-center' }
      }
    },
    {
      accessorKey: 'order_number',
      header: '№',
      cell: rowData => {
        const orderNumber = rowData.row.index + 1 + (currentPage - 1) * 10;
        return <div>{orderNumber}</div>;
      },
      meta: {
        headerProps: { style: { width: '5%' } },
        cellProps: { className: 'text-900 p-2' }
      }
    },
    {
      accessorKey: 'orders',
      header: t('application_number'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'sender',
      header: `${t('sender')} (${t('applicant')})`,
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'delivery',
      header: t('delivery_man'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'currency',
      header: t('currency'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'status',
      header: t('status'),
      cell: rowData => {
        const { status, status_pid } = rowData.row.original;

        let styles = { bg: '#e0f7fa', text: '#00796b' };

        if (status_pid === 10 || status_pid === 6 || status_pid === 4) {
          styles = { bg: '#fce4ec', text: '#c2185b' };
        } else if (status_pid === 1 || status_pid === 2) {
          styles = { bg: '#fffde7', text: '#fbc02d' };
        }

        return (
          <Button
            style={{
              backgroundColor: styles.bg,
              color: styles.text,
              whiteSpace: 'nowrap'
            }}
          >
            {status}
          </Button>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'create_date',
      header: t('date_request'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'delivered_date',
      header: t('date_delivery'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'recipient_approved_date',
      header: t('date_completion'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: outgoingData,
    columns: outgoingTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  useEffect(() => {
    refetch();
  }, [dateFrom, dateTo, optionSenderId, optionStatusId]);

  return (
    <AdvanceTableProvider {...table}>
      <div className="mt-4 mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
        <AdvanceTable
          tableProps={{ className: 'phoenix-table fs-9' }}
          navigateTo={navigateTo}
          isLoading={loading}
        />
        <AdvanceTableCPagination
          count={pageCount}
          page={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </div>
    </AdvanceTableProvider>
  );
};

export default OutgoingTable;
